import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card, Button } from "react-bootstrap";
import { RootState } from "@reducers/rootReducer";
import { useUpdateOpenRouterKeyMutation } from "@generated";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import { OpenRouterKeyForm } from "../common/Form";
import { INITIAL_FORM_STATE, OpenRouterKeyFormState } from "../common/types";
import { formatOpenRouterKeyData } from "../common/helpers";
import { useGetOpenRouterKey } from "../common/api";
import "../style.css";

const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const keyId = id || "";
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const [updateOpenRouterKey] = useUpdateOpenRouterKeyMutation();

  const {
    openRouterKey,
    isLoading: isFetching,
    refetch,
  } = useGetOpenRouterKey(keyId);

  const [formState, setFormState] =
    useState<OpenRouterKeyFormState>(INITIAL_FORM_STATE);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);

  useEffect(() => {
    if (openRouterKey && keyId) {
      setFormState((prev) => {
        if (prev.id) return prev;
        return { ...openRouterKey };
      });
    }
  }, [openRouterKey, keyId]);

  const handleSave = () => {
    if (!formState.keyType) return;

    setLoading(true);
    const data = formatOpenRouterKeyData(formState);

    updateOpenRouterKey({
      slug: accountSlug,
      id: keyId as unknown as number,
      body: { data },
    })
      .unwrap()
      .then(() => {
        setSuccessMessage("Open router key updated successfully!");
        setShowSuccessAlert(true);
        setTimeout(() => {
          refetch();
        }, 200);
      })
      .catch((error) => {
        console.error("API Error:", error);
        setErrorMessage(
          error?.data?.errors?.[0]?.detail || "An error occurred."
        );
        setShowFailAlert(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (isFetching) {
    return <SmallSpinner />;
  }

  return (
    <>
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      <Card
        style={{
          padding: "16px",
          borderRadius: "4px",
          border: "1px solid var(--gray-3-stroke, #D7D7D7)",
          background: "var(--white, #FFF)",
          boxShadow: "0px 2px 3px 0px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Card.Header
          style={{
            backgroundColor: "#fff",
            lineHeight: "40px",
            display: "block",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div
              className="accordianTitle"
              style={{ height: "40px", margin: "0", alignContent: "center" }}
            >
              <Button
                className="cancelButton"
                variant="secondary"
                onClick={() => navigate("/admin/open_router_keys")}
              >
                Back
              </Button>
              Edit Open Router Key
            </div>
            <div className="actionBtns">
              <Button
                className="regButton"
                onClick={handleSave}
                disabled={loading || !formState.keyType}
                variant="primary"
              >
                {loading ? <SmallSpinner /> : "Save"}
              </Button>
            </div>
          </div>
          <hr />
        </Card.Header>
        <Card.Body style={{ padding: "0px 16px 16px 16px" }}>
          <OpenRouterKeyForm
            formState={formState}
            setFormState={setFormState}
            editing={true}
          />
        </Card.Body>
      </Card>
    </>
  );
};

export default Edit;
